import axios from 'axios';

const normalizeApiBaseUrl = (raw) => {
  const base = String(raw || '').trim().replace(/\/+$/, '');
  if (!base) return 'http://localhost:5000/api';
  // If caller already provided .../api keep it, otherwise append /api
  if (base.toLowerCase().endsWith('/api')) return base;
  return `${base}/api`;
};

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const API_URL = normalizeApiBaseUrl(API_BASE);

// Log API URL in development to help debug
if (process.env.NODE_ENV === 'development') {
  console.log('API URL configured:', API_URL);
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors — use event so Layout can navigate without full reload
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getCurrentUser: () => api.get('/auth/me'),
};

// Leads APIs
export const leadsAPI = {
  searchLeads: (params) => api.get('/leads/search', { params }),
  bulkAssign: (data) => api.post('/leads/bulk-assign', data),
  getAll: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  calculateScore: (id) => api.post(`/leads/${id}/calculate-score`),
  bulkUpdate: (data) => api.post('/leads/bulk-update', data),
  checkDuplicates: (params) => api.get('/leads/check-duplicates', { params }),
  transfer: (id, data) => api.post(`/leads/${id}/transfer`, data),
  updateTags: (id, tags) => api.post(`/leads/${id}/tags`, { tags }),
  validateImport: (formData) => api.post('/leads/import/validate', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  importLeads: (data) => api.post('/leads/import', data),
  downloadTemplate: () => api.get('/leads/import/template', { responseType: 'blob' }),
  downloadErrorReport: (data) => api.post('/leads/import/error-report', data, { responseType: 'blob' }),
  getImportHistory: (params) => api.get('/leads/import/history', { params }),
  exportLeads: (params) => {
    const useJson = params && (params.preview === true || params.format === 'json');
    return api.get('/leads/export', { params, responseType: useJson ? 'json' : 'blob' });
  },
  getStatsBar: (params) => api.get('/leads/stats-bar', { params }),
};

// Activities APIs
export const activitiesAPI = {
  getByLead: (leadId) => api.get(`/activities/lead/${leadId}`),
  create: (data) => api.post('/activities', data),
  getRecent: (limit) => api.get('/activities/recent', { params: { limit } }),
  delete: (id) => api.delete(`/activities/${id}`),
};

// Tasks APIs
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  getOverdue: () => api.get('/tasks/overdue'),
};

// Properties APIs
export const propertiesAPI = {
  getAll: (params) => api.get('/properties', { params }),
  getById: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
};

// Team Notes APIs
export const teamNotesAPI = {
  getByLead: (leadId) => api.get(`/team-notes/lead/${leadId}`),
  create: (data) => api.post('/team-notes', data),
  update: (id, data) => api.put(`/team-notes/${id}`, data),
  delete: (id) => api.delete(`/team-notes/${id}`),
};

// Projects APIs
export const projectsAPI = {
  getAll: () => api.get('/projects'),
  create: (data) => api.post('/projects', data),
};

// Notifications APIs
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Analytics APIs
export const analyticsAPI = {
  getDashboardStats: (params) => api.get('/analytics/dashboard-stats', { params }),
  getDashboard: (params) => api.get('/analytics/dashboard', { params }),
  getLeadSources: () => api.get('/analytics/lead-sources'),
  getStatusDistribution: () => api.get('/analytics/lead-status-distribution'),
  getMonthlyTrends: (months) => api.get('/analytics/monthly-trends', { params: { months } }),
  getAgentPerformance: () => api.get('/analytics/agent-performance'),
  getPropertyTypeAnalysis: () => api.get('/analytics/property-type-analysis'),
  getRecentActivities: (limit) => api.get('/analytics/recent-activities', { params: { limit } }),
  getConversionFunnel: () => api.get('/analytics/conversion-funnel'),
  getPipeline: () => api.get('/analytics/pipeline'),
  getSources: () => api.get('/analytics/sources'),
  getTrends: (months) => api.get('/analytics/trends', { params: { months } }),
  // New dashboard endpoints
  getDashboardMain: () => api.get('/analytics/dashboard-main'),
  getTodayFollowups: () => api.get('/analytics/today-followups'),
  getAgentLeaderboard: () => api.get('/analytics/agent-leaderboard'),
  getRecentLeads: () => api.get('/analytics/recent-leads'),
  getRecentProperties: () => api.get('/analytics/recent-properties'),
  getRecentCommunications: () => api.get('/analytics/recent-communications'),
  // New analytics endpoints
  getKpiPeriod: (period) => api.get('/analytics/kpi-period', { params: { period } }),
  getRevenueTrend: (period) => api.get('/analytics/revenue-trend', { params: { period } }),
  getLeadSourcesPeriod: (period) => api.get('/analytics/lead-sources-period', { params: { period } }),
  getPropertyPerformance: (period) => api.get('/analytics/property-performance', { params: { period } }),
  getAgentPerformancePeriod: (period, sortBy, sortOrder) => api.get('/analytics/agent-performance-period', { params: { period, sortBy, sortOrder } }),
  // Dashboard additions
  getOverdueCount: () => api.get('/analytics/overdue-count'),
  getDealsClosingSoon: () => api.get('/analytics/deals-closing-soon'),
  getConversionRate: () => api.get('/analytics/conversion-rate'),
  getUpcomingTasks: () => api.get('/analytics/upcoming-tasks'),
  // Agent score
  getAgentScorePeriod: (period) => api.get('/analytics/agent-score-period', { params: { period } }),
};

// Tasks mark done
export const tasksAPIExtended = {
  markAsDone: (id) => api.put(`/analytics/tasks/${id}/done`),
};

// Documents APIs
export const documentsAPI = {
  getAll: (params) => api.get('/documents', { params }),
  getById: (id) => api.get(`/documents/${id}`),
  getByLead: (leadId) => api.get(`/documents/lead/${leadId}`),
  getByProperty: (propertyId) => api.get(`/documents/property/${propertyId}`),
  upload: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/documents/${id}`),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
};

// Settings APIs
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  getPublic: () => api.get('/settings/public'),
  getByKey: (key) => api.get(`/settings/${key}`),
  update: (key, value) => api.put(`/settings/${key}`, { value }),
  bulkUpdate: (settings) => api.put('/settings', settings),
};

// Users APIs
export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Audit log (admin only)
export const auditLogAPI = {
  getAll: (params) => api.get('/audit-log', { params }),
};

// Timeline APIs (unified system events + notes)
export const timelineAPI = {
  getByLead: (leadId) => api.get(`/timeline/lead/${leadId}`),
  addNote: (leadId, data) => api.post(`/timeline/lead/${leadId}/notes`, data),
  updateNote: (noteId, data) => api.put(`/timeline/notes/${noteId}`, data),
  deleteNote: (noteId) => api.delete(`/timeline/notes/${noteId}`),
  logSystem: (leadId, event_type) => api.post(`/timeline/lead/${leadId}/system`, { event_type }),
  getAdminFeed: (params) => api.get('/timeline/admin/feed', { params }),
};

// AI APIs
export const aiAPI = {
  generateLeadNotesSummary: (leadId) => api.post(`/ai/leads/${leadId}/notes-summary`),
};

// Communications APIs
export const communicationsAPI = {
  getMessages: (leadId) => api.get(`/communications/${leadId}`),
  getCount: (leadId) => api.get(`/communications/${leadId}/count`),
  sendMessage: (leadId, content) => api.post(`/communications/${leadId}`, { content }),
  logCommunication: (leadId, data) => api.post(`/communications/${leadId}`, data),
};

export default api;